import type { SupportedGeometry, VectorFeature, VectorSourceDescriptor } from '../../data/model';
import { isVectorSource, type SourceDescriptor, type SpatialExtent } from '../../data/model';
import type { LayerDefinition } from '../../project/model';

export const SEARCH_RESULT_LIMIT = 8;

export interface FeatureSearchResult {
  readonly layerId: string;
  readonly layerName: string;
  readonly featureIndex: number;
  readonly label: string;
  readonly match: string;
  readonly geometry: SupportedGeometry;
}

export function parseDecimalCoordinates(input: string): {
  readonly latitude: number;
  readonly longitude: number;
  readonly order: 'latitude, longitude' | 'longitude, latitude';
} | null {
  const match = /^\s*([+-]?(?:\d+(?:\.\d*)?|\.\d+))\s*,\s*([+-]?(?:\d+(?:\.\d*)?|\.\d+))\s*$/.exec(
    input,
  );
  if (!match) return null;
  const first = Number(match[1]);
  const second = Number(match[2]);
  if (Math.abs(first) <= 90 && Math.abs(second) <= 180)
    return { latitude: first, longitude: second, order: 'latitude, longitude' };
  if (Math.abs(first) > 90 && Math.abs(first) <= 180 && Math.abs(second) <= 90)
    return { latitude: second, longitude: first, order: 'longitude, latitude' };
  return null;
}

export function extentForGeometry(geometry: SupportedGeometry | null): SpatialExtent | null {
  if (!geometry) return null;
  let west = Infinity;
  let south = Infinity;
  let east = -Infinity;
  let north = -Infinity;
  const add = (point: readonly number[]) => {
    const [longitude, latitude] = point;
    if (
      longitude === undefined ||
      latitude === undefined ||
      !Number.isFinite(longitude) ||
      !Number.isFinite(latitude)
    )
      return;
    west = Math.min(west, longitude);
    east = Math.max(east, longitude);
    south = Math.min(south, latitude);
    north = Math.max(north, latitude);
  };
  switch (geometry.type) {
    case 'Point':
      add(geometry.coordinates);
      break;
    case 'MultiPoint':
    case 'LineString':
      geometry.coordinates.forEach(add);
      break;
    case 'MultiLineString':
    case 'Polygon':
      geometry.coordinates.forEach((part) => part.forEach(add));
      break;
    case 'MultiPolygon':
      geometry.coordinates.forEach((polygon) => polygon.forEach((part) => part.forEach(add)));
      break;
  }
  return Number.isFinite(west) ? { west, south, east, north, crossesAntimeridian: false } : null;
}

function matchingScalar(
  feature: VectorFeature,
  query: string,
): { label: string; match: string } | null {
  const properties = feature.properties;
  if (!properties) return null;
  let firstScalar: string | null = null;
  for (const [field, value] of Object.entries(properties)) {
    if (typeof value !== 'string' && typeof value !== 'number' && typeof value !== 'boolean')
      continue;
    const display = String(value);
    if (!firstScalar && display) firstScalar = display;
    if (display.toLocaleLowerCase().includes(query)) {
      const named = properties.name ?? properties.Name ?? properties.title;
      const label = typeof named === 'string' && named.trim() ? named : (firstScalar ?? display);
      return { label, match: `${field}: ${display}` };
    }
  }
  return null;
}

/** Scan canonical features in small batches, retaining only the first limit + 1 matches. */
export async function searchProjectFeatures(
  sources: readonly SourceDescriptor[],
  layers: readonly LayerDefinition[],
  query: string,
  isCurrent: () => boolean = () => true,
  limit = SEARCH_RESULT_LIMIT,
): Promise<{ readonly results: readonly FeatureSearchResult[]; readonly hasMore: boolean }> {
  const needle = query.trim().toLocaleLowerCase();
  if (needle.length < 2) return { results: [], hasMore: false };
  const sourceById = new Map<string, VectorSourceDescriptor>(
    sources.filter(isVectorSource).map((source) => [source.id, source]),
  );
  const results: FeatureSearchResult[] = [];
  let scanned = 0;
  for (const layer of layers) {
    const source = sourceById.get(layer.sourceId);
    if (!source) continue;
    for (let index = 0; index < source.data.features.length; index += 1) {
      if (!isCurrent()) return { results: [], hasMore: false };
      const feature = source.data.features[index];
      const match = feature ? matchingScalar(feature, needle) : null;
      if (feature && match && extentForGeometry(feature.geometry)) {
        if (results.length >= limit) return { results, hasMore: true };
        results.push({
          layerId: layer.id,
          layerName: layer.name,
          featureIndex: index,
          ...match,
          geometry: feature.geometry!,
        });
      }
      scanned += 1;
      if (scanned % 500 === 0) await new Promise<void>((resolve) => setTimeout(resolve, 0));
    }
  }
  return { results, hasMore: false };
}
